import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { jobSettingsSchema, insertJobSettingsSchema, type JobSettings, type InsertJobSettings } from '@/lib/schema';

// Legacy type for compatibility with existing UI
export interface JobSettingsData {
  employmentTypes: string[];
  categories: string[];
  skills: string[];
  locations: string[];
  priorities: string[];
  defaultRewardAmount: number;
  minRewardAmount: number;
  maxRewardAmount: number;
  referralExpirationDays: number;
  lastUpdated: Date;
  updatedBy: string;
}

// Get job settings singleton document
export const getJobSettings = async (): Promise<JobSettings | null> => {
  const settingsDoc = await getDoc(doc(db, 'job_settings', 'global'));
  if (!settingsDoc.exists()) return null;
  
  const data = settingsDoc.data();
  return jobSettingsSchema.parse({
    id: 'global',
    ...data,
    lastUpdated: data.lastUpdated?.toDate() || new Date()
  }) as JobSettings;
};

// Create or update job settings
export const updateJobSettings = async (data: InsertJobSettings, updatedBy: string): Promise<JobSettings | null> => {
  const parsed = insertJobSettingsSchema.parse(data);
  
  const settingsData = {
    ...parsed,
    lastUpdated: serverTimestamp(),
    updatedBy
  };
  
  await setDoc(doc(db, 'job_settings', 'global'), settingsData);
  return getJobSettings();
};

// Get job settings in the legacy format for UI compatibility
export const getJobSettingsLegacy = async (): Promise<JobSettingsData | null> => {
  const settings = await getJobSettings();
  if (!settings) return null;
  
  return {
    employmentTypes: settings.employmentTypes,
    categories: settings.categories,
    skills: settings.skills,
    locations: settings.locations,
    priorities: settings.priorities,
    defaultRewardAmount: settings.defaultRewardAmount,
    minRewardAmount: settings.minRewardAmount,
    maxRewardAmount: settings.maxRewardAmount,
    referralExpirationDays: settings.referralExpirationDays,
    lastUpdated: settings.lastUpdated,
    updatedBy: settings.updatedBy
  };
};

// Initialize default job settings if they don't exist
export const initializeJobSettings = async (adminUserId: string): Promise<JobSettings | null> => {
  const existing = await getJobSettings();
  if (existing) return existing;
  
  const defaultSettings: InsertJobSettings = {
    employmentTypes: ['full-time', 'part-time', 'contract', 'internship'],
    categories: [
      'engineering',
      'design', 
      'sales',
      'marketing',
      'product',
      'operations',
      'finance',
      'hr',
      'legal',
      'customer-support'
    ],
    priorities: ['low', 'normal', 'high', 'urgent'],
    skills: [
      // Engineering
      'JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'Java', 'Go',
      'AWS', 'Docker', 'Kubernetes', 'GraphQL', 'REST API', 'SQL', 'NoSQL',
      // Design
      'UI/UX Design', 'Figma', 'Adobe Creative Suite', 'Sketch', 'Prototyping',
      'User Research', 'Wireframing', 'Design Systems',
      // Marketing
      'Digital Marketing', 'SEO', 'SEM', 'Content Marketing', 'Social Media',
      'Email Marketing', 'Analytics', 'Growth Hacking',
      // Sales
      'B2B Sales', 'B2C Sales', 'Account Management', 'Lead Generation',
      'CRM', 'Sales Operations', 'Business Development',
      // General
      'Project Management', 'Agile', 'Scrum', 'Leadership', 'Communication',
      'Problem Solving', 'Team Management', 'Strategic Planning'
    ],
    locations: [
      'Remote',
      'New York, NY',
      'San Francisco, CA',
      'Los Angeles, CA',
      'Chicago, IL',
      'Boston, MA',
      'Seattle, WA',
      'Austin, TX',
      'Denver, CO',
      'Miami, FL',
      'Atlanta, GA',
      'Toronto, ON',
      'London, UK',
      'Berlin, Germany',
      'Amsterdam, Netherlands',
      'Singapore',
      'Sydney, Australia'
    ],
    defaultRewardAmount: 1000,
    minRewardAmount: 500,
    maxRewardAmount: 10000,
    referralExpirationDays: 90
  };
  
  return updateJobSettings(defaultSettings, adminUserId);
};

// Helper functions for UI components
export const getEmploymentTypes = async (): Promise<string[]> => {
  const settings = await getJobSettings();
  return settings?.employmentTypes || ['full-time', 'part-time', 'contract', 'internship'];
};

export const getJobCategories = async (): Promise<string[]> => {
  const settings = await getJobSettings();
  return settings?.categories || ['engineering', 'design', 'sales', 'marketing', 'product'];
};

export const getSkillsList = async (): Promise<string[]> => {
  const settings = await getJobSettings();
  return settings?.skills || [];
};

export const getLocationsList = async (): Promise<string[]> => {
  const settings = await getJobSettings();
  return settings?.locations || ['Remote'];
};

export const getPrioritiesList = async (): Promise<string[]> => {
  const settings = await getJobSettings();
  return settings?.priorities || ['low', 'normal', 'high', 'urgent'];
};

export const getRewardLimits = async (): Promise<{ min: number; max: number; default: number }> => {
  const settings = await getJobSettings();
  return {
    min: settings?.minRewardAmount || 500,
    max: settings?.maxRewardAmount || 10000,
    default: settings?.defaultRewardAmount || 1000
  };
};

// Check which job settings options are currently in use by jobs
export const checkOptionsInUse = async (field: string, options: string[]): Promise<Record<string, boolean>> => {
  const inUse: Record<string, boolean> = {};
  
  // Initialize all options as not in use
  options.forEach(option => {
    inUse[option] = false;
  });
  
  try {
    // Check jobs collection for usage
    const jobsQuery = query(collection(db, 'jobs'));
    const jobsSnapshot = await getDocs(jobsQuery);
    
    jobsSnapshot.docs.forEach(doc => {
      const jobData = doc.data();
      const fieldValue = jobData[field];
      
      if (fieldValue && options.includes(fieldValue)) {
        inUse[fieldValue] = true;
      }
    });
  } catch (error) {
    console.error('Error checking options in use:', error);
  }
  
  return inUse;
};